import { useEffect, useState } from "react";
import Layout from "@/components/app/Layout";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Trash2, RotateCcw, Briefcase, User, Clock, AlertTriangle, Loader2 } from "lucide-react";

const OWNER_ROLES = new Set(["owner", "admin"]);

function daysLeft(iso) {
  if (!iso) return 30;
  const deleted = new Date(iso).getTime();
  const purgeAt = deleted + 30 * 24 * 60 * 60 * 1000;
  const left = Math.max(0, Math.ceil((purgeAt - Date.now()) / (24 * 60 * 60 * 1000)));
  return left;
}

export default function RecyclePage() {
  const { user } = useAuth();
  const canPurge = !!user && OWNER_ROLES.has(user.role);
  const [data, setData] = useState({ jobs: [], candidates: [] });
  const [loading, setLoading] = useState(true);
  const [emptying, setEmptying] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/recycle").then((r) => { setData(r.data); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const restoreJob = async (id) => {
    try { await api.post(`/recycle/jobs/${id}/restore`); toast.success("Job restored"); load(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const restoreCand = async (id) => {
    try { await api.post(`/recycle/candidates/${id}/restore`); toast.success("Candidate restored"); load(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const purgeJob = async (id) => {
    if (!window.confirm("Permanently delete this job and its candidates? This can NOT be undone.")) return;
    try { await api.delete(`/recycle/jobs/${id}`); toast.success("Purged"); load(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const purgeCand = async (id) => {
    if (!window.confirm("Permanently delete this candidate? This can NOT be undone.")) return;
    try { await api.delete(`/recycle/candidates/${id}`); toast.success("Purged"); load(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const emptyBin = async () => {
    const totalItems = (data.jobs?.length || 0) + (data.candidates?.length || 0);
    if (totalItems === 0) return;
    if (!window.confirm(`Permanently delete ALL ${totalItems} item${totalItems === 1 ? "" : "s"} in the recycle bin? This includes every related interview recording and session. This can NOT be undone.`)) return;
    setEmptying(true);
    try {
      const { data: res } = await api.delete("/recycle");
      toast.success(`Recycle bin emptied · ${res.jobs_purged} job${res.jobs_purged === 1 ? "" : "s"} and ${res.candidates_purged} candidate${res.candidates_purged === 1 ? "" : "s"} purged`);
      load();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Failed to empty recycle bin");
    } finally { setEmptying(false); }
  };

  const totalItems = (data.jobs?.length || 0) + (data.candidates?.length || 0);

  return (
    <Layout
      actions={
        canPurge && totalItems > 0 ? (
          <button
            data-testid="empty-recycle-bin"
            onClick={emptyBin}
            disabled={emptying}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white transition-colors disabled:opacity-60"
          >
            {emptying ? <Loader2 className="w-4 h-4 animate-spin"/> : <Trash2 className="w-4 h-4"/>}
            Empty recycle bin
          </button>
        ) : null
      }
    >
      <div className="mb-6">
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Workspace</div>
        <h1 className="font-display text-3xl font-bold">Recycle Bin</h1>
        <p className="text-sm text-slate-400 mt-1">Deleted jobs and candidates are kept here for 30 days, then permanently purged. Restore anytime before then.</p>
      </div>

      {loading ? (
        <div className="tp-card p-8 text-center text-slate-500">Loading…</div>
      ) : data.jobs.length === 0 && data.candidates.length === 0 ? (
        <div className="tp-card p-12 text-center">
          <Trash2 className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <div className="font-display text-lg mb-1">Recycle bin is empty</div>
          <div className="text-sm text-slate-400">Nothing has been deleted in the last 30 days.</div>
        </div>
      ) : (
        <>
          {data.jobs.length > 0 && (
            <div className="tp-card p-6 mb-4" data-testid="recycle-jobs">
              <div className="flex items-center gap-2 mb-4"><Briefcase className="w-4 h-4 text-indigo-300"/><div className="font-display font-semibold">Jobs</div><span className="text-xs text-slate-500">({data.jobs.length})</span></div>
              <div className="space-y-2">
                {data.jobs.map((j) => (
                  <div key={j.id} className="flex items-center justify-between p-3 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)]">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{j.title}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3"/> deleted {new Date(j.deleted_at).toLocaleDateString()} · <span className={daysLeft(j.deleted_at) <= 7 ? "text-amber-400" : ""}>{daysLeft(j.deleted_at)} days left</span></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button data-testid={`restore-job-${j.id}`} onClick={() => restoreJob(j.id)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"><RotateCcw className="w-3.5 h-3.5"/> Restore</button>
                      <button data-testid={`purge-job-${j.id}`} onClick={() => purgeJob(j.id)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-rose-500/30 text-rose-300 hover:bg-rose-500/10"><Trash2 className="w-3.5 h-3.5"/> Purge</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.candidates.length > 0 && (
            <div className="tp-card p-6" data-testid="recycle-candidates">
              <div className="flex items-center gap-2 mb-4"><User className="w-4 h-4 text-cyan-300"/><div className="font-display font-semibold">Candidates</div><span className="text-xs text-slate-500">({data.candidates.length})</span></div>
              <div className="space-y-2">
                {data.candidates.map((c) => (
                  <div key={c.id} className="flex items-center justify-between p-3 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)]">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{c.name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3"/> deleted {new Date(c.deleted_at).toLocaleDateString()} · <span className={daysLeft(c.deleted_at) <= 7 ? "text-amber-400" : ""}>{daysLeft(c.deleted_at)} days left</span></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button data-testid={`restore-cand-${c.id}`} onClick={() => restoreCand(c.id)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"><RotateCcw className="w-3.5 h-3.5"/> Restore</button>
                      <button data-testid={`purge-cand-${c.id}`} onClick={() => purgeCand(c.id)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-rose-500/30 text-rose-300 hover:bg-rose-500/10"><Trash2 className="w-3.5 h-3.5"/> Purge</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 text-xs text-slate-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-amber-400"/> Only workspace owners can permanently purge items.</div>
        </>
      )}
    </Layout>
  );
}
